import { Box, Button, Grid, Typography, styled, useTheme } from "@mui/material";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { Loader, colors } from "@zfp-akademie-a-s/zfp-design";
import { ResetIcon } from "Components/_Shared/Icons/Icons";
import {
  getGetGoalModelingDetailQueryKey,
  getGetModelingAvailableAmountQueryKey,
  getGetModelingAvailableAssetAmountQueryKey,
  getGetModelingRecapitulationQueryKey,
  getGoalModelingDetail,
  useGetAnalysisGoals,
  useReallocateModelingAssets,
  useResetModelingAllocatedAmounts,
  useResetModelingAllocatedAssetAmounts,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  AssetReallocationBodySection,
  type GoalModeling,
  type GoalOverview,
  GoalOverviewHorizon,
  GoalOverviewLifecycleStatus,
  GoalOverviewProgressStatus,
} from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrencyWithoutCode } from "Infrastructure/Utils/CurrencyUtils";
import { type FC, useContext } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { GoalModelingRow } from "./GoalModelingRow";

const HeaderContainer = styled(Grid)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  background: ${colors.backgroundTertiary};
  border-radius: ${(props) => props.theme.spacing(Radius.M, Radius.M, 0, 0)};
  padding-bottom: ${(props) => props.theme.spacing(Spacing.XL)};
`;

const RowDashed = styled(Grid)`
  position: relative;

  & span {
    z-index: 1;
    position: relative;
  }

  &::before {
    content: '';
    position: absolute;
    top: 21px;
    width: 100%;
    height: 1px;
    background-image: repeating-linear-gradient(to right, ${colors.borderLight} 0, ${colors.borderLight} 10px, transparent 10px, transparent 15px);
  }
`;

const CellWithBackground = styled(Grid)`
  background: ${colors.backgroundTertiary};
  padding: ${(props) => props.theme.spacing(0, Spacing.L)};
`;

type GoalModelingRowData = {
  readonly goal: GoalOverview;
  readonly modelingGoalId: string;
  readonly modeling: GoalModeling;
};

const sortGoalModelingRows = (prev: GoalModelingRowData, next: GoalModelingRowData) => {
  const prevDuration = (prev.modeling.overview?.goalDuration?.years ?? 0) + (prev.modeling.overview?.goalDuration?.months ?? 0) / 12;
  const nextDuration = (next.modeling.overview?.goalDuration?.years ?? 0) + (next.modeling.overview?.goalDuration?.months ?? 0) / 12;

  return prevDuration - nextDuration;
};

export const GoalModelingTable: FC = () => {
  const { readOnly } = useContext(AnalysisDetailContext);
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { t } = useResource();
  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };

  const { data: goalsData } = useGetAnalysisGoals(analysisId, versionId);
  const activeGoals = (goalsData?.goals ?? []).filter(
    (goal) => goal.lifecycleStatus === GoalOverviewLifecycleStatus.active && goal.progressStatus === GoalOverviewProgressStatus.in_progress,
  );
  const modelingGoalIds = activeGoals.map((goal) => goal.guid);

  const { data: modelings, pending: areModelingsPending } = useQueries({
    queries: modelingGoalIds.map((goalId) => ({
      queryKey: getGetGoalModelingDetailQueryKey(analysisId, versionId, goalId),
      queryFn: () => getGoalModelingDetail(analysisId, versionId, goalId),
    })),
    combine: (results) => ({
      data: results.map((result) => result.data),
      pending: results.some((result) => result.isPending),
    }),
  });

  const invalidateAllGoalModelingDetails = () => {
    for (const goalId of modelingGoalIds) {
      queryClient.invalidateQueries({ queryKey: getGetGoalModelingDetailQueryKey(analysisId, versionId, goalId) });
    }
  };

  const rows: GoalModelingRowData[] = activeGoals
    .map((goal, index) => {
      const modeling = modelings[index];
      const modelingGoalId = modelingGoalIds[index];
      return modeling ? { goal, modelingGoalId, modeling } : null;
    })
    .filter((row): row is GoalModelingRowData => row !== null);

  const { mutate: resetAllocatedAmounts } = useResetModelingAllocatedAmounts();
  const { mutate: resetAllocatedAssetAmounts } = useResetModelingAllocatedAssetAmounts();
  const { mutate: reallocateAssets } = useReallocateModelingAssets();

  if (areModelingsPending) {
    return <Loader />;
  }

  const shortTermRows = rows.filter((row) => row.goal.horizon === GoalOverviewHorizon.short);
  const midTermRows = rows.filter((row) => row.goal.horizon === GoalOverviewHorizon.middle);
  const longTermRows = rows.filter((row) => row.goal.horizon === GoalOverviewHorizon.long);

  const totalAssignedRecurring = rows.reduce((prev, row) => prev + (row.modeling.allocatedAmount?.amount?.value ?? 0), 0);

  const totalAssignedFinancial = rows.reduce((prev, row) => {
    return prev + row.modeling.allocatedAssetAmounts.reduce((assignedFinancial, item) => assignedFinancial + (item.amount?.value ?? 0), 0);
  }, 0);

  const renderRow = (row: GoalModelingRowData) => (
    <GoalModelingRow
      readOnly={readOnly}
      key={row.modelingGoalId}
      goalId={row.modelingGoalId}
      goalOverview={row.goal}
      name={row.goal.name}
      targetAmount={row.modeling.overview?.targetAmount ?? null}
      goalDuration={row.modeling.overview?.goalDuration ?? null}
      interestRate={row.modeling.overview?.appreciationRate ?? 0}
      inflationValue={row.modeling.overview?.inflationValue ?? 0}
      feeValue={row.modeling.overview?.feeValue ?? 0}
      allocatedAmount={row.modeling.allocatedAmount}
      allocatedAssetAmounts={row.modeling.allocatedAssetAmounts}
      coverageSection={row.modeling.coverageSection ?? null}
    />
  );

  return (
    <Box>
      <Grid container spacing={Spacing.M} height={theme.spacing(20)} alignItems="center">
        <HeaderContainer offset={4} size={4}>
          <Box mr={Spacing.M}>
            <Button
              variant="contained"
              disabled={readOnly}
              onClick={() => {
                reallocateAssets(
                  { analysisId, versionId, data: { section: AssetReallocationBodySection.financial_asset } },
                  {
                    onSuccess: () => {
                      queryClient.invalidateQueries({ queryKey: getGetModelingAvailableAmountQueryKey(analysisId, versionId) });
                      queryClient.invalidateQueries({ queryKey: getGetModelingAvailableAssetAmountQueryKey(analysisId, versionId) });
                      queryClient.invalidateQueries({ queryKey: getGetModelingRecapitulationQueryKey(analysisId, versionId) });
                      invalidateAllGoalModelingDetails();
                      toast.success(t(Resources.AnalysisDetail.Modeling.AssetsReallocated));
                    },
                    onError: () => toast.error(t(Resources.Common.Error)),
                  },
                );
              }}
            >
              {t(Resources.AnalysisDetail.Modeling.Redistribute)}
            </Button>
          </Box>
          <Button
            variant="contained"
            disabled={readOnly}
            color="secondary"
            endIcon={<ResetIcon sx={{ color: "text.primary" }} />}
            onClick={() => {
              resetAllocatedAssetAmounts(
                { analysisId, versionId },
                {
                  onSuccess: () => {
                    queryClient.invalidateQueries({ queryKey: getGetModelingAvailableAssetAmountQueryKey(analysisId, versionId) });
                    invalidateAllGoalModelingDetails();
                    toast.success(t(Resources.AnalysisDetail.Modeling.OneTimeReset));
                  },
                  onError: () => toast.error(t(Resources.Common.Error)),
                },
              );
            }}
          >
            {t(Resources.AnalysisDetail.Modeling.Reset)}
          </Button>
        </HeaderContainer>
        <HeaderContainer size={4}>
          <Box mr={Spacing.M}>
            <Button
              variant="contained"
              disabled={readOnly}
              onClick={() => {
                reallocateAssets(
                  { analysisId, versionId, data: { section: AssetReallocationBodySection.available_amount } },
                  {
                    onSuccess: () => {
                      queryClient.invalidateQueries({ queryKey: getGetModelingAvailableAmountQueryKey(analysisId, versionId) });
                      queryClient.invalidateQueries({ queryKey: getGetModelingAvailableAssetAmountQueryKey(analysisId, versionId) });
                      queryClient.invalidateQueries({ queryKey: getGetModelingRecapitulationQueryKey(analysisId, versionId) });
                      invalidateAllGoalModelingDetails();
                      toast.success(t(Resources.AnalysisDetail.Modeling.AssetsReallocated));
                    },
                    onError: () => toast.error(t(Resources.Common.Error)),
                  },
                );
              }}
            >
              {t(Resources.AnalysisDetail.Modeling.Redistribute)}
            </Button>
          </Box>
          <Button
            variant="contained"
            disabled={readOnly}
            color="secondary"
            endIcon={<ResetIcon sx={{ color: "text.primary" }} />}
            onClick={() => {
              resetAllocatedAmounts(
                { analysisId, versionId },
                {
                  onSuccess: () => {
                    queryClient.invalidateQueries({ queryKey: getGetModelingAvailableAmountQueryKey(analysisId, versionId) });
                    invalidateAllGoalModelingDetails();
                    toast.success(t(Resources.AnalysisDetail.Modeling.RecurringReset));
                  },
                  onError: () => toast.error(t(Resources.Common.Error)),
                },
              );
            }}
          >
            {t(Resources.AnalysisDetail.Modeling.Reset)}
          </Button>
        </HeaderContainer>
      </Grid>
      {shortTermRows.length > 0 ? (
        <RowDashed container spacing={Spacing.M} height={theme.spacing(10)} alignItems="center">
          <Grid size={4}>
            <Typography ml={16} variant="caption" fontWeight={600}>
              {t(Resources.AnalysisDetail.Goals.Timeline.ShortTermGoals)}
            </Typography>
          </Grid>
          <CellWithBackground size={4} height="100%" />
          <CellWithBackground size={4} height="100%" />
        </RowDashed>
      ) : undefined}
      {shortTermRows.sort(sortGoalModelingRows).map(renderRow)}

      {midTermRows.length > 0 ? (
        <RowDashed container spacing={Spacing.M} height={theme.spacing(10)} alignItems="center">
          <Grid size={4}>
            <Typography ml={16} variant="caption" fontWeight={600}>
              {t(Resources.AnalysisDetail.Goals.Timeline.MidTermGoals)}
            </Typography>
          </Grid>
          <CellWithBackground size={4} height="100%" />
          <CellWithBackground size={4} height="100%" />
        </RowDashed>
      ) : undefined}
      {midTermRows.sort(sortGoalModelingRows).map(renderRow)}
      {longTermRows.length > 0 ? (
        <RowDashed container spacing={Spacing.M} height={theme.spacing(10)} alignItems="center">
          <Grid size={4}>
            <Typography ml={16} variant="caption" fontWeight={600}>
              {t(Resources.AnalysisDetail.Goals.Timeline.LongTermPoint)}
            </Typography>
          </Grid>
          <CellWithBackground size={4} height="100%" />
          <CellWithBackground size={4} height="100%" />
        </RowDashed>
      ) : undefined}
      {longTermRows.sort(sortGoalModelingRows).map(renderRow)}
      <Grid container mt={Spacing.M}>
        <Grid offset={4} size={4}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mx={Spacing.XL}
            pt={Spacing.XL}
            height={theme.spacing(Spacing.XXXL)}
            sx={{ borderTop: `1px solid ${colors.borderLight}` }}
          >
            <Typography variant="body2" fontWeight={600}>
              {t(Resources.Common.Total)}
            </Typography>
            <Box display="flex" alignItems="center">
              <Typography variant="body2" fontWeight={500}>
                {formatCurrencyWithoutCode(totalAssignedFinancial)}
              </Typography>
              &nbsp;
              <Typography variant="caption" color="secondary">
                {t(Resources.Common.CZKSuffix)}
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid size={4}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mx={Spacing.XL}
            pt={Spacing.XL}
            height={theme.spacing(Spacing.XXXL)}
            sx={{ borderTop: `1px solid ${colors.borderLight}` }}
          >
            <Typography variant="body2" fontWeight={600}>
              {t(Resources.Common.Total)}
            </Typography>
            <Box display="flex" alignItems="center">
              <Typography variant="body2" fontWeight={500}>
                {formatCurrencyWithoutCode(totalAssignedRecurring)}
              </Typography>
              &nbsp;
              <Typography variant="caption" color="secondary">
                {t(Resources.Common.CZKSuffix)}/{t(Resources.Common.Month)}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
